/**
 * サイドバーのナビゲーション構成。
 *
 * **性質の違う 3 群を見出しで分ける。** PJ（いま何を変えているか）・成果物（仕様として
 * 書いたもの）・マスターデータ（成果物が参照する分類）は、開く理由も開く頻度も違う。8 行を
 * 見出し無しで一列に置いていたときは、PJ 一覧の隣に業務ツリーが、その隣にアクターが並び、
 * 「いま自分がどの層のものを開こうとしているのか」が並びから読めなかった。
 *
 * **折り畳まない。** 共有デザインシステムは親項目に子をぶら下げる折り畳みを持つが、
 * 使わない。成果物の 2 行は日常的に行き来する先で、隠すと開くたびに 1 クリック増える。
 * アコーディオンは可視性を下げ操作コストを上げる（NN/g）ので、行が 6 本（担当者には 7 本）で収まるこの規模では
 * 見出しで区切るだけにする。
 *
 * **PJ モードでは PJ 一覧を出さず、その PJ の変更一覧を出す。** PJ を選んでいる間に PJ
 * 一覧へ戻る導線を置くと、いま適用している PJ から黙って抜ける経路がサイドバーに常設される
 * ことになる。PJ を抜ける導線はヘッダーの PJ バッジが持っている（`PjModeBadge`）ので、
 * サイドバー側は「いまの PJ で何を変えたか」へ寄せる。
 *
 * **Epic の折りたたみツリーはサイドバーに置かない。** 業務マップ（`/business-map`）が同じ木を
 * 持っており、サイドバーにも展開すると同じ木が 2 箇所に描かれる（core-principles の一次表現
 * 原則）。ナビ側だけが古い並びで残る余地も生まれる。US へは業務マップから入る。
 *
 * **横断検索の行も持たない。** 探すのは各一覧の中の絞り込みで完結させる。型の混ざった検索結果を
 * 読むより、対象の一覧を開いてから絞るほうが当たりが早い。
 *
 * **業務ツリー（`/epics`）と業務（`/businesses`）は 1 行の業務マップ（`BUSINESS_MAP_ROUTE`）へ
 * 畳んだ。** 2 行は Epic の木と業務の平らな並びという同じ集合への別々の入口で、どちらを開けば
 * よいかが行の名前から決まらなかった。業務マップは同じ集合をレーンに分けて 1 画面に載せ、
 * 左から右へ絞る。**一覧から開ける詳細はサイドバーの行にしない** —
 * 業務の詳細は業務マップのカードから開く先であって、サイドバーが持つ入口ではない。
 *
 * 各項目の遷移先は `route-paths.ts` の定数から引き、`app.tsx` の `createRoute` が同じ定数で
 * 実ルートを張る。リンク先を "#" や実在しないパスにすると、ルートが増えたときに
 * 「壊れていたことに気付かないまま残る」ため、定数を経由しない文字列は書かない。
 *
 * 共有デザインシステムの `SidebarNavItem.path` は `string` なので、リンク描画側がルーターの
 * 型付き `to` に渡すには実在ルートへ絞り込む必要がある。型アサーションで黙らせるとナビ定義の
 * 綴り誤りが利用者側の 404 として初めて表面化するため、解決は実行時に確かめる。
 *
 * 用語集はマスターデータへ置く。用語は成果物が参照する語彙の集合であり、アクター・ドメインと
 * 同じ層のものだからである。マスターデータの群は アクター ／ ドメイン ／ 用語集 の順に並べる。
 * **ステージは行にしない** — 業務マップの
 * レーンから開く詳細で、一覧から開ける詳細はサイドバーの行にしない（上の決まり）。成果物の側に置くと、業務マップ・画面一覧という
 * 「書いたもの」の並びに、参照される側が 1 つだけ混ざる。
 *
 * **動詞と単位も行にしない**（Dhik 2026-09-29「サイドバーから動詞と単位を消してほしい。それぞれの
 * 説明から詳細ページに飛べるのは残しておいて」）。どちらも業務詳細 ・ 用語詳細などの説明の中の
 * リンクから詳細（`/masters/verb/<id>` ・ `/masters/unit/<id>`）を開く先で、一覧（`/verbs` ・
 * `/units`）のルートは残すが、サイドバーの常設の入口にはしない。
 *
 * **承認一覧は担当者にだけ出す 4 群目（管理）に置く。** 承認は限られた担当者の操作で、
 * 担当者でない人のサイドバーに「承認一覧」を出すと、押した先で拒否されるだけの行が常設される。
 * 権限は API の照合（`getApprovalAccess`）が持ち、ここはその結果を受け取って群ごと出し分ける。
 * 3 群のどれにも混ぜないのは、承認が PJ でも成果物でもマスターデータでもなく、それらを跨いで
 * 判断する管理の操作だからである。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business Spec Console のサイドバーを、PJ・成果物・マスターデータの 3 群と担当者だけの管理 1 群として組み立てる。
 * @graph-connects none
 */
import type { SidebarNavGroup } from '@cortex/web-design-system/components/layout/Sidebar';
import {
  AppWindow,
  BookOpen,
  ClipboardCheck,
  Compass,
  FolderTree,
  GitCompareArrows,
  LayoutDashboard,
  Tickets,
  Users,
} from 'lucide-react';

import {
  ACTORS_ROUTE,
  APPROVALS_ROUTE,
  BUSINESS_MAP_ROUTE,
  DOMAINS_ROUTE,
  HOME_ROUTE,
  SCREENS_ROUTE,
  TERMS_ROUTE,
  PJS_ROUTE,
} from '../../routes/route-paths.js';

/** ラベルの翻訳関数。i18next の `t` をそのまま渡せる最小の形。 */
type TranslateLabel = (key: string) => string;

/**
 * ナビゲーションが解決しうる静的パスの集合。
 *
 * ホームは行としては出さないが、解決できないパスの受け皿として集合に残す。ナビは常に押せ
 * なければならないので、押しても何も起きない項目を作るより確実に在る入口へ倒す。
 * @graph-connects none
 */
export const NAV_ROUTE_PATHS = [
  HOME_ROUTE,
  PJS_ROUTE,
  BUSINESS_MAP_ROUTE,
  SCREENS_ROUTE,
  TERMS_ROUTE,
  ACTORS_ROUTE,
  DOMAINS_ROUTE,
  APPROVALS_ROUTE,
] as const;

/** ナビゲーションが解決しうる静的パス。 */
export type NavRoutePath = (typeof NAV_ROUTE_PATHS)[number];

/**
 * 文字列がナビゲーションの静的パスかを判定する。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ナビ項目のパスが実在する静的ルートかを判定する。
 * @graph-connects none
 */
export function isNavRoutePath(path: string): path is NavRoutePath {
  return (NAV_ROUTE_PATHS as readonly string[]).includes(path);
}

/** ナビ項目が指す遷移先。PJ の変更一覧だけがパスパラメータを持つ。 */
export type NavTarget =
  | { readonly kind: 'static'; readonly to: NavRoutePath }
  | { readonly kind: 'pj-diff'; readonly pjNumber: string };

/**
 * ナビ項目のパスを、ルーターへ渡せる遷移先へ解決する。
 *
 * 静的ルートのほかに、PJ の変更一覧（`/pjs/<PJ 番号>`）だけがパスパラメータを持つ。
 * PJ モードのサイドバーはその PJ の変更一覧を指すので、番号を含む実 URL をナビ項目の
 * `path` に載せ、ここで番号を取り出してルーターの型付き遷移へ写す。実 URL をそのまま
 * `to` へ渡さないのは、ルーターが定義パス（`/pjs/$pjNumber`）で遷移先を解決する
 * ためである。`path` に実 URL を置くのは、共有デザインシステムが現在地との一致で選択中の行を
 * 決めており、定義パスのままでは PJ を開いていても行が選択中にならないからである。
 *
 * 解決できないパスはホームへ倒す。ナビ項目は常に押せる必要があり、押しても何も起きない状態を
 * 作らない。
 *
 * @param path ナビ項目のパス
 * @returns 遷移先
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business ナビ項目のパスを静的ルートまたは PJ の変更一覧へ解決し、解決できないものはホームへ倒す。
 * @graph-connects isNavRoutePath [calls] 実在する静的ルートかの判定
 */
export function navLinkTarget(path: string): NavTarget {
  if (isNavRoutePath(path)) return { kind: 'static', to: path };

  const pjNumber = path.startsWith(`${PJS_ROUTE}/`) ? path.slice(PJS_ROUTE.length + 1) : '';

  if (pjNumber !== '' && !pjNumber.includes('/')) {
    return { kind: 'pj-diff', pjNumber };
  }

  return { kind: 'static', to: HOME_ROUTE };
}

/**
 * PJ の変更一覧を指すナビ項目のパス。
 *
 * @param pjNumber PJ 番号
 * @returns PJ の変更一覧の実 URL
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business PJ の変更一覧を指すナビ項目のパスを組み立てる。
 * @graph-connects none
 */
export function pjDiffNavPath(pjNumber: string): string {
  return `${PJS_ROUTE}/${pjNumber}`;
}

/**
 * 翻訳済みラベルでサイドバーの 3 群（担当者には 4 群）を組み立てる。
 *
 * 取得結果（Epic 木など）は受け取らない。取得の成否でナビの中身が変わらないようにするためで、
 * 以前は Epic の取得が失敗するとサイドバーの一部が消えていた。PJ 番号だけは例外で受け取る —
 * これは取得結果ではなく現在地から読める事実であり（`useCurrentPj`）、失敗しない。
 *
 * **承認できるかも受け取る。** これは取得結果だが、失敗や取得中に消えるのは管理の 1 群だけで、
 * 3 群は影響を受けない。担当者でない人（と照合の前）には出さないのが正しい挙動なので、
 * 取得の成否で 3 群が消えた過去の事故とは性質が違う。
 *
 * @param t 翻訳関数
 * @param pjNumber いま適用している PJ 番号。適用していなければ undefined
 * @param canApprove 本人が承認を積める担当者か。true のときだけ管理の群を足す
 * @returns PJ・成果物・マスターデータの 3 群。担当者には管理の 1 群を末尾に足した 4 群
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business サイドバーを、PJ・成果物・マスターデータの 3 群と担当者だけの管理 1 群として組み立てる。
 * @graph-connects pjDiffNavPath [calls] PJ の変更一覧のパス
 */
export function buildNavGroups(
  t: TranslateLabel,
  pjNumber?: string,
  canApprove = false,
): SidebarNavGroup[] {
  const pjItem =
    pjNumber === undefined || pjNumber === ''
      ? {
          key: 'pjs-list',
          label: t('nav.pjsList'),
          path: PJS_ROUTE,
          icon: <Tickets className="h-4 w-4" aria-hidden="true" />,
        }
      : {
          key: 'pj-changes',
          label: t('nav.pjChanges'),
          path: pjDiffNavPath(pjNumber),
          icon: <GitCompareArrows className="h-4 w-4" aria-hidden="true" />,
        };

  return [
    {
      label: t('nav.groupPj'),
      items: [pjItem],
    },
    {
      label: t('nav.groupArtifacts'),
      dividerBefore: true,
      items: [
        {
          key: 'business-map',
          label: t('nav.businessMap'),
          path: BUSINESS_MAP_ROUTE,
          icon: <LayoutDashboard className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'screens',
          label: t('nav.screens'),
          path: SCREENS_ROUTE,
          icon: <AppWindow className="h-4 w-4" aria-hidden="true" />,
        },
      ],
    },
    {
      label: t('nav.groupMasters'),
      dividerBefore: true,
      items: [
        {
          key: 'actors',
          label: t('nav.actors'),
          path: ACTORS_ROUTE,
          icon: <Users className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'domains',
          label: t('nav.domains'),
          path: DOMAINS_ROUTE,
          icon: <FolderTree className="h-4 w-4" aria-hidden="true" />,
        },
        {
          key: 'terms',
          label: t('nav.terms'),
          path: TERMS_ROUTE,
          icon: <BookOpen className="h-4 w-4" aria-hidden="true" />,
        },
      ],
    },
    ...(canApprove
      ? [
          {
            label: t('nav.groupAdmin'),
            dividerBefore: true,
            items: [
              {
                key: 'approvals',
                label: t('nav.approvals'),
                path: APPROVALS_ROUTE,
                icon: <ClipboardCheck className="h-4 w-4" aria-hidden="true" />,
              },
            ],
          },
        ]
      : []),
  ];
}

/** サイドバーのロゴスロット。 @graph-connects none */
export function NavLogo() {
  return <Compass className="h-5 w-5 text-[var(--color-accent-primary)]" aria-hidden="true" />;
}
